import React from 'react';
import classnames from 'classnames';

import { ChipTypes } from '../chips';
import Checkbox from '../forms/checkbox';

import Devices from './devices';
import styles from './content-list-item.css';
import ListItem from './list-item';
import { ContentContainer } from './ContentContainer';
import { StatsContainer } from './StatsContainer';
import { ListItemBanner } from './ListItemBanner';

export interface Props {
  /** URL where the user will be redirected when clicking on the item */
  href?: string;
  /** Provides visual elements that indicate whether the content is published or not */
  published: boolean;
  /** Title of the content */
  title: string;
  /** URL where the content is being served */
  url: string;
  /** Number of visitors the content has */
  visitors: number;
  /** Number of conversions the content has */
  conversions: number;
  /** Conversion rate in percentage of the content */
  conversionRate: number;
  /** Show device information */
  showDevices?: boolean;
  /** When true, it indicates the content type has a desktop version */
  desktop?: boolean;
  /** When true, it indicates the content type has a mobile version */
  mobile?: boolean;
  /** When true shows the checkmark of the item */
  showCheck?: boolean;
  /** Whether the checkmark of the item is checked or not */
  checked?: boolean;
  /** Displays a label to specify the content type. Please refer to Chips for more information */
  type?: ChipTypes;
  /** Adds additional classes to the component. */
  className?: string;
  /** Contents of the flyout menu */
  flyoutMenu?: React.ReactElement<any>;
  /** Displays an icon to specify the domain of the page is a Wordpress domain */
  wordpressEnabled?: boolean;
  /** When true, indicates there are unpublished changes on the page */
  hasUnpublishedChanges?: boolean;
  /** When higher than zero, indicates there are integration errors on the page */
  integrationErrorsCount?: number;
  /** Provides a data-testid */
  'data-testid'?: string;
}

const PublishedBar = ({ published }: { published: boolean }) => (
  <hr
    className={classnames(styles.publishedBar, published && styles.published)}
    data-testid="publish-bar"
  />
);

export const ContentListItem = ({
  href = '',
  published = false,
  title,
  url,
  visitors,
  conversions,
  conversionRate,
  showDevices = false,
  showCheck = false,
  checked = false,
  type,
  className,
  mobile = false,
  desktop = false,
  wordpressEnabled = false,
  hasUnpublishedChanges = false,
  integrationErrorsCount = 0,
  ...rest
}: Props) => (
  <div className={styles.contentItem}>
    <ListItem
      className={classnames(styles.item, className)}
      data-testid="content-list-item"
      {...{ href, showCheck }}
      {...rest}
    >
      <PublishedBar {...{ published }} />
      {showCheck && <Checkbox checked={checked} className={styles.checkboxWrapper} />}
      <ContentContainer
        {...{
          title,
          wordpressEnabled,
          url,
          type
        }}
      />
      {showDevices && <Devices mobile={mobile} desktop={desktop} />}
      <StatsContainer {...{ visitors, conversions, conversionRate }} />
    </ListItem>
    {hasUnpublishedChanges && (
      <ListItemBanner type="unpublished">
        This page has&nbsp;
        <strong>Unpublished Changes</strong>
      </ListItemBanner>
    )}
    {integrationErrorsCount > 0 && (
      <ListItemBanner type="integration">
        {`${integrationErrorsCount} Integration errors have been logged`}
      </ListItemBanner>
    )}
  </div>
);

export default ContentListItem;
